// Headless export registration for Conjoint Consumer Journey (single-output).
// Two live components behind the same keys, switched by params.view (mirrors the
// legacy pptx.coffee branch): 'lite' → the importance-% HC LiteChart (exportable
// via ChartContainer's built-in attachment), else the rich D3 Chart, which draws
// the whole visualization (percents, batches, sidebar) into ONE <svg> serialized
// by prepareForExport. Keys `cjc` (Claim Choice) and `cjd` (Claim Duel) are the
// legacy pptx.coffee dispatch keys (Option A).
//
// NOTE: legacy multi-output (formatAllSVG, cluster×persona matrix) is commented out
// in the coffee source and not active in React — reality is single-output. Re-expand
// this phase only if that path is revived.
// Spec: .aidev/tasks/headless-charts-sdk-spike/docs/tasks/06-cj.md §4

import { createElement, type ComponentType } from 'react'

import {
  ExportableCharts,
  type ExportableChart
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'
import { commonHcProps } from '@aytm/app/stats/page/question-views/components/charts/lib/hc-exportable'

import CjChart from '@aytm/app/stats/page/question-views/cjc/components/chart/Chart'
import LiteChart from '@aytm/app/stats/page/question-views/cjc/components/lite-chart/LiteChart'
import { calcMainPackages } from '@aytm/app/stats/page/question-views/cjc/components/chart/lib/chart-utils'

// currentPackage's real default is the median package, computed from the answer
// coefficients — it is NOT persisted. On the live page the legacy Backbone block does
// this (block.coffee); the leaf React Chart just consumes the prop and would NaN on an
// empty string. We recreate the block's fallback here so the driver can forward the
// report-param cjc{key} verbatim (empty when the user never picked a package) instead
// of re-deriving conjoint math on the backend. mainPackages[2] is the median.
const resolveCurrentPackage = (explicit: unknown, data: any, mode: string, persona: number): string => {
  if (typeof explicit === 'string' && explicit.length) return explicit
  const median = calcMainPackages(data.answer, mode, persona)[2]
  return Array.isArray(median) ? median.join('.') : ''
}

// Legacy pptx.coffee splits conjoint into two branches by view: 'lite' renders the
// importance-% HC chart (generic getItemParams path), anything else the rich D3
// visual. The registry maps one Component per key, so a tiny switcher keeps both
// behind the same legacy dispatch keys — the driver forwards `view` in params.
const CjExportSwitch = (props: any) =>
  createElement((props.view === 'lite' ? LiteChart : CjChart) as ComponentType<any>, props)

const cjEntry: ExportableChart = {
  Component: CjExportSwitch,
  toProps: (data: any, params: any) => {
    if ('lite' === params.view) {
      return {
        ...commonHcProps(data, params),
        view: 'lite',
        persona: params.persona ?? 0
      }
    }

    const mode = params.mode || 'exp'
    const persona = params.persona ?? 0
    // params.currentPackage (explicit override) wins; else data's; else median.
    const currentPackage = resolveCurrentPackage(params.currentPackage ?? data.currentPackage, data, mode, persona)

    return {
      answer: data.answer,
      batches: data.batches,
      // Sibling export dropdown is not part of the <svg>; disable it in headless.
      features: data.features || { export: { disabled: true } },
      mode,
      persona,
      // hiddenBatches stays a plain array — the live Chart mutates it via
      // indexOf/splice/push and calcData reads .length/.includes (NOT a Set).
      hiddenBatches: Array.isArray(data.hiddenBatches) ? data.hiddenBatches : [],
      currentPackage,
      scale: params.scale ?? true,
      center: params.center ?? true,
      sortOrder: params.sortOrder || 'iasc',
      colorTheme: data.colorTheme,
      chartLegendMode: params.chartLegendMode || 'long',
      // Interactive callbacks stay undefined in headless.
      onPackageChange: undefined,
      onHiddenBatchesChange: undefined,
      onExport: undefined
    }
  }
}

ExportableCharts.register('cjc', cjEntry)
ExportableCharts.register('cjd', cjEntry)
